import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useLoading } from '../../contexts/LoadingContext';
import { updateProductSale, removeProductSale, getActiveSales, searchProductsForSales } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';

const SalesPage = () => {
  const { startLoading, stopLoading } = useLoading();
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [saleForm, setSaleForm] = useState({
    discountPercentage: '',
    isSaleActive: false,
    saleStartDate: '',
    saleEndDate: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch products with sales
  useEffect(() => {
    fetchSalesProducts();
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('product-dropdown');
      const searchInput = document.getElementById('product-search-input');
      
      if (isDropdownOpen && dropdown && !dropdown.contains(event.target) && !searchInput?.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  const fetchSalesProducts = async () => {
    try {
      startLoading();
      const response = await getActiveSales();
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching sales products:', error);
      toast.error('Failed to fetch sales products');
    } finally {
      stopLoading();
    }
  };

  // Server-side search function
  const fetchSearchResults = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      setIsSearching(true);
      const response = await searchProductsForSales(query, 8);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Trigger search when debounced query changes
  useEffect(() => {
    fetchSearchResults(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const handleOpenModal = (product = null, bulkMode = false) => {
    if (product) {
      setEditingSale(product);
      setSelectedProduct(product);
      setSelectedProducts([]);
      setIsBulkMode(false);
      setIsDropdownOpen(false);
      setSearchQuery('');
      setSaleForm({
        discountPercentage: product.discountPercentage || '',
        isSaleActive: product.isSaleActive || false,
        saleStartDate: product.saleStartDate ? new Date(product.saleStartDate).toISOString().slice(0, 16) : '',
        saleEndDate: product.saleEndDate ? new Date(product.saleEndDate).toISOString().slice(0, 16) : ''
      });
    } else {
      setEditingSale(null);
      setSelectedProduct(null);
      setSelectedProducts([]);
      setIsBulkMode(bulkMode);
      setIsDropdownOpen(false);
      setSearchQuery('');
      setSaleForm({
        discountPercentage: '',
        isSaleActive: false,
        saleStartDate: '',
        saleEndDate: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSale(null);
    setSelectedProduct(null);
    setSelectedProducts([]);
    setIsBulkMode(false);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setFormErrors({});
    setSaleForm({
      discountPercentage: '',
      isSaleActive: false,
      saleStartDate: '',
      saleEndDate: ''
    });
  };

  const handleProductSelect = (product) => {
    if (isBulkMode) {
      setSelectedProducts(prev => {
        const exists = prev.find(p => p._id === product._id);
        if (exists) {
          return prev.filter(p => p._id !== product._id);
        } else {
          return [...prev, product];
        }
      });
      // Keep dropdown open in bulk mode for multiple selections
    } else {
      setSelectedProduct(product);
      setSearchQuery(product.name);
      setIsDropdownOpen(false); // Close dropdown after single selection
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    if (!saleForm.discountPercentage || !saleForm.saleStartDate || !saleForm.saleEndDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      startLoading();
      let successCount = 0;
      let errorCount = 0;

      for (const product of selectedProducts) {
        try {
          const discountPercentage = parseFloat(saleForm.discountPercentage);
          if (discountPercentage <= 0 || discountPercentage >= 100) {
            errorCount++;
            continue;
          }
          await updateProductSale(product._id, saleForm);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error updating sale for ${product.name}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully created sales for ${successCount} products`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to create sales for ${errorCount} products`);
      }

      handleCloseModal();
      fetchSalesProducts();
    } catch (error) {
      console.error('Error in bulk sale creation:', error);
      toast.error('Failed to create bulk sales');
    } finally {
      stopLoading();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormErrors({});
    
    if (!selectedProduct) {
      setFormErrors({ general: 'Please select a product' });
      return;
    }

    if (saleForm.isSaleActive && (!saleForm.discountPercentage || !saleForm.saleStartDate || !saleForm.saleEndDate)) {
      setFormErrors({ general: 'Please fill in all required fields' });
      return;
    }

    if (saleForm.discountPercentage) {
      const discountPercentage = parseFloat(saleForm.discountPercentage);
      if (discountPercentage <= 0 || discountPercentage >= 100) {
        setFormErrors({ discountPercentage: 'Discount percentage must be between 1 and 99' });
        return;
      }
    }

    // Validate dates
    if (saleForm.saleStartDate && saleForm.saleEndDate) {
      const startDate = new Date(saleForm.saleStartDate);
      const endDate = new Date(saleForm.saleEndDate);
      const now = new Date();
      
      if (endDate <= startDate) {
        setFormErrors({ saleEndDate: 'Sale end date must be after start date' });
        return;
      }
      
      if (startDate < now) {
        setFormErrors({ saleStartDate: 'Sale start date cannot be in the past' });
        return;
      }
    }

    try {
      startLoading();
      await updateProductSale(selectedProduct._id, saleForm);
      toast.success(editingSale ? 'Sale updated successfully' : 'Sale created successfully');
      handleCloseModal();
      fetchSalesProducts();
    } catch (error) {
      console.error('Error updating sale:', error);
      
      // Handle specific error messages from backend
      const errorMessage = error.message || error.response?.data?.message || 'Failed to update sale';
      
      // Check for specific field errors
      if (errorMessage.includes('start date cannot be in the past')) {
        setFormErrors({ saleStartDate: 'Sale start date cannot be in the past' });
      } else if (errorMessage.includes('end date must be after start date')) {
        setFormErrors({ saleEndDate: 'Sale end date must be after start date' });
      } else if (errorMessage.includes('discount percentage')) {
        setFormErrors({ discountPercentage: errorMessage });
      } else {
        setFormErrors({ general: errorMessage });
      }
    } finally {
      stopLoading();
    }
  };

  const handleRemoveSale = async (productId) => {
    if (!window.confirm('Are you sure you want to remove this sale?')) {
      return;
    }

    try {
      startLoading();
      await removeProductSale(productId);
      toast.success('Sale removed successfully');
      fetchSalesProducts();
    } catch (error) {
      console.error('Error removing sale:', error);
      toast.error('Failed to remove sale');
    } finally {
      stopLoading();
    }
  };

  const handleToggleSale = async (product) => {
    try {
      startLoading();
      const updatedForm = {
        discountPercentage: product.discountPercentage,
        isSaleActive: !product.isSaleActive,
        saleStartDate: product.saleStartDate,
        saleEndDate: product.saleEndDate
      };
      await updateProductSale(product._id, updatedForm);
      toast.success(`Sale ${!product.isSaleActive ? 'enabled' : 'disabled'}`);
      fetchSalesProducts();
    } catch (error) {
      console.error('Error toggling sale:', error);
      toast.error('Failed to toggle sale');
    } finally {
      stopLoading();
    }
  };

  const displayProducts = searchResults;

  const formatCurrency = (amount) => `Rs. ${amount?.toLocaleString() || 0}`;

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getDiscountPercentage = (regularPrice, salePrice) => {
    if (!salePrice || !regularPrice) return 0;
    return Math.round(((regularPrice - salePrice) / regularPrice) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">Sales Management</h1>
          <p className="text-admin-slate-600 dark:text-admin-slate-400">Manage product sales and discounts</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleOpenModal(null, true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Bulk Sale
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="bg-admin-slate-600 hover:bg-admin-slate-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Sale
          </button>
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-white dark:bg-admin-slate-800 rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
            <thead className="bg-admin-slate-50 dark:bg-admin-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Sale Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-admin-slate-800 divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
              {products.map((product) => (
                <tr key={product._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        <img
                          className="h-12 w-12 rounded-lg object-cover"
                          src={product.images?.[0] || '/placeholder-product.jpg'}
                          alt={product.name}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                          {product.name}
                        </div>
                        <div className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
                          {product.brand?.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-admin-slate-900 dark:text-admin-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="line-through text-admin-slate-500">
                          {formatCurrency(product.regularPrice)}
                        </span>
                        <span className="font-semibold text-red-600">
                          {formatCurrency(product.regularPrice * (1 - product.discountPercentage / 100))}
                        </span>
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                          -{product.discountPercentage}%
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-admin-slate-900 dark:text-admin-slate-100">
                      <div>Start: {new Date(product.saleStartDate).toLocaleDateString()}</div>
                      <div>End: {new Date(product.saleEndDate).toLocaleDateString()}</div>
                      <div className="text-xs text-admin-slate-500">
                        {getTimeRemaining(product.saleEndDate)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      product.isSaleActive 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                    }`}>
                      {product.isSaleActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenModal(product)}
                        className="text-admin-slate-600 hover:text-admin-slate-900 dark:text-admin-slate-400 dark:hover:text-admin-slate-100"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleSale(product)}
                        className={`px-2 py-1 text-xs rounded ${
                          product.isSaleActive
                            ? 'bg-red-100 text-red-800 hover:bg-red-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {product.isSaleActive ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleRemoveSale(product._id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sale Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleCloseModal}></div>
            
            <div className="relative bg-white dark:bg-admin-slate-800 rounded-xl max-w-3xl w-full shadow-2xl transform transition-all flex flex-col max-h-[90vh]">
              {/* Modal Header - Fixed */}
              <div className="flex items-center justify-between p-6 border-b border-admin-slate-200 dark:border-admin-slate-700 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                      {editingSale ? 'Edit Sale' : isBulkMode ? 'Create Bulk Sales' : 'Create New Sale'}
                    </h3>
                    <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
                      {editingSale 
                        ? 'Update sale details and pricing' 
                        : isBulkMode 
                          ? 'Set up time-limited discounts for multiple products' 
                          : 'Set up a time-limited discount for a product'
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 text-admin-slate-400 hover:text-admin-slate-600 dark:hover:text-admin-slate-300 hover:bg-admin-slate-100 dark:hover:bg-admin-slate-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <form id="sale-form" onSubmit={isBulkMode ? handleBulkSubmit : handleSubmit} className="p-6 space-y-8">
                  {/* General Error Display */}
                  {formErrors.general && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-red-800 dark:text-red-200">
                          {formErrors.general}
                        </span>
                      </div>
                    </div>
                  )}
                {/* Product Selection */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-admin-slate-700 dark:text-admin-slate-300">
                      {isBulkMode ? 'Select Products' : 'Select Product'}
                    </label>
                    <div className="flex items-center gap-4">
                      {isBulkMode && (
                        <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                          {selectedProducts.length} selected
                        </span>
                      )}
                      <span className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                        {searchResults.length} products found
                      </span>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-admin-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      id="product-search-input"
                      type="text"
                      placeholder="Type to search products..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsDropdownOpen(e.target.value.length >= 2);
                      }}
                      onFocus={() => setIsDropdownOpen(searchQuery.length >= 2)}
                      className="w-full pl-10 pr-4 py-3 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-admin-slate-700 dark:text-white transition-colors"
                    />
                    {isDropdownOpen && searchQuery && (
                      <div 
                        id="product-dropdown"
                        className="absolute z-20 w-full mt-2 bg-white dark:bg-admin-slate-700 border border-admin-slate-200 dark:border-admin-slate-600 rounded-lg shadow-xl max-h-64 overflow-y-auto"
                      >
                        {isSearching ? (
                          <div className="p-4 text-center text-admin-slate-500 dark:text-admin-slate-400">
                            <div className="flex items-center justify-center gap-2">
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Searching...
                            </div>
                          </div>
                        ) : searchQuery.length < 2 ? (
                          <div className="p-4 text-center text-admin-slate-500 dark:text-admin-slate-400">
                            Type at least 2 characters to search
                          </div>
                        ) : displayProducts.length === 0 ? (
                          <div className="p-4 text-center text-admin-slate-500 dark:text-admin-slate-400">
                            No products found
                          </div>
                        ) : (
                          displayProducts.map((product) => {
                            const isSelected = isBulkMode 
                              ? selectedProducts.find(p => p._id === product._id)
                              : selectedProduct?._id === product._id;
                            
                            return (
                              <button
                                key={product._id}
                                type="button"
                                onClick={() => handleProductSelect(product)}
                                className={`w-full px-4 py-3 text-left flex items-center gap-3 border-b border-admin-slate-100 dark:border-admin-slate-600 last:border-b-0 transition-colors ${
                                  isSelected 
                                    ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700' 
                                    : 'hover:bg-primary-50 dark:hover:bg-primary-900/20'
                                }`}
                              >
                                {isBulkMode && (
                                  <div className="flex-shrink-0">
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                      isSelected 
                                        ? 'bg-primary-600 border-primary-600' 
                                        : 'border-admin-slate-300 dark:border-admin-slate-600'
                                    }`}>
                                      {isSelected && (
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <img
                                  src={product.images?.[0] || '/placeholder-product.jpg'}
                                  alt={product.name}
                                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100 truncate">
                                    {product.name}
                                  </div>
                                  <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                                    {product.brand?.name} • {formatCurrency(product.regularPrice)}
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  {isBulkMode ? (
                                    <span className="text-xs text-admin-slate-400">
                                      {isSelected ? 'Selected' : 'Click to select'}
                                    </span>
                                  ) : (
                                    <svg className="w-4 h-4 text-admin-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Single Product Selection */}
                  {!isBulkMode && selectedProduct && (
                    <div className="p-4 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg border border-primary-200 dark:border-primary-700">
                      <div className="flex items-center gap-4">
                        <img
                          src={selectedProduct.images?.[0] || '/placeholder-product.jpg'}
                          alt={selectedProduct.name}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-admin-slate-900 dark:text-admin-slate-100 mb-1">
                            {selectedProduct.name}
                          </div>
                          <div className="text-sm text-admin-slate-600 dark:text-admin-slate-400 mb-2">
                            {selectedProduct.brand?.name}
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-lg font-bold text-primary-600 dark:text-primary-400">
                              {formatCurrency(selectedProduct.regularPrice)}
                            </div>
                            <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                              Regular Price
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProduct(null);
                            setSearchQuery('');
                          }}
                          className="p-2 text-admin-slate-400 hover:text-admin-slate-600 dark:hover:text-admin-slate-300 hover:bg-white dark:hover:bg-admin-slate-700 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Bulk Product Selection */}
                  {isBulkMode && selectedProducts.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300">
                          Selected Products ({selectedProducts.length})
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedProducts([])}
                          className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Clear All
                        </button>
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {selectedProducts.map((product) => (
                          <div key={product._id} className="flex items-center gap-3 p-3 bg-admin-slate-50 dark:bg-admin-slate-700 rounded-lg">
                            <img
                              src={product.images?.[0] || '/placeholder-product.jpg'}
                              alt={product.name}
                              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100 truncate">
                                {product.name}
                              </div>
                              <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                                {formatCurrency(product.regularPrice)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedProducts(prev => prev.filter(p => p._id !== product._id))}
                              className="p-1 text-admin-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Discount Percentage Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-admin-slate-700 dark:text-admin-slate-300">
                      Discount Percentage
                    </label>
                    {saleForm.discountPercentage && (
                      <div className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                        {saleForm.discountPercentage}% OFF
                      </div>
                    )}
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-admin-slate-500 dark:text-admin-slate-400 text-sm">%</span>
                    </div>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      max="99"
                      value={saleForm.discountPercentage}
                      onChange={(e) => {
                        setSaleForm({ ...saleForm, discountPercentage: e.target.value });
                        // Clear error when user starts typing
                        if (formErrors.discountPercentage) {
                          setFormErrors({ ...formErrors, discountPercentage: '' });
                        }
                      }}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-admin-slate-700 dark:text-white transition-colors ${
                        formErrors.discountPercentage 
                          ? 'border-red-500 dark:border-red-400' 
                          : 'border-admin-slate-300 dark:border-admin-slate-600'
                      }`}
                      placeholder="10"
                    />
                  </div>
                  {formErrors.discountPercentage && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {formErrors.discountPercentage}
                    </p>
                  )}
                  
                  {selectedProduct && saleForm.discountPercentage && (
                    <div className="p-3 bg-admin-slate-50 dark:bg-admin-slate-700 rounded-lg">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-admin-slate-600 dark:text-admin-slate-400">Regular Price:</span>
                        <span className="line-through text-admin-slate-500">{formatCurrency(selectedProduct.regularPrice)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm font-semibold mt-1">
                        <span className="text-admin-slate-700 dark:text-admin-slate-300">Sale Price:</span>
                        <span className="text-primary-600 dark:text-primary-400">
                          {formatCurrency(selectedProduct.regularPrice * (1 - parseFloat(saleForm.discountPercentage || 0) / 100))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-admin-slate-600 dark:text-admin-slate-400">You Save:</span>
                        <span className="text-green-600 dark:text-green-400 font-semibold">
                          {formatCurrency(selectedProduct.regularPrice * parseFloat(saleForm.discountPercentage || 0) / 100)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sale Period */}
                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-admin-slate-700 dark:text-admin-slate-300">
                    Sale Period
                  </label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">
                        Start Date & Time
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-admin-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="datetime-local"
                          value={saleForm.saleStartDate}
                          onChange={(e) => {
                            setSaleForm({ ...saleForm, saleStartDate: e.target.value });
                            // Clear error when user starts typing
                            if (formErrors.saleStartDate) {
                              setFormErrors({ ...formErrors, saleStartDate: '' });
                            }
                          }}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-admin-slate-700 dark:text-white transition-colors ${
                            formErrors.saleStartDate 
                              ? 'border-red-500 dark:border-red-400' 
                              : 'border-admin-slate-300 dark:border-admin-slate-600'
                          }`}
                        />
                      </div>
                      {formErrors.saleStartDate && (
                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formErrors.saleStartDate}
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">
                        End Date & Time
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-admin-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="datetime-local"
                          value={saleForm.saleEndDate}
                          onChange={(e) => {
                            setSaleForm({ ...saleForm, saleEndDate: e.target.value });
                            // Clear error when user starts typing
                            if (formErrors.saleEndDate) {
                              setFormErrors({ ...formErrors, saleEndDate: '' });
                            }
                          }}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-admin-slate-700 dark:text-white transition-colors ${
                            formErrors.saleEndDate 
                              ? 'border-red-500 dark:border-red-400' 
                              : 'border-admin-slate-300 dark:border-admin-slate-600'
                          }`}
                        />
                      </div>
                      {formErrors.saleEndDate && (
                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formErrors.saleEndDate}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {saleForm.saleStartDate && saleForm.saleEndDate && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <div className="flex items-center gap-2 text-sm">
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-blue-700 dark:text-blue-300 font-medium">
                          Sale Duration: {Math.ceil((new Date(saleForm.saleEndDate) - new Date(saleForm.saleStartDate)) / (1000 * 60 * 60 * 24))} days
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Enable Sale Toggle */}
                <div className="flex items-center justify-between p-4 bg-admin-slate-50 dark:bg-admin-slate-700 rounded-lg border border-admin-slate-200 dark:border-admin-slate-600">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <label htmlFor="isSaleActive" className="block text-sm font-semibold text-admin-slate-700 dark:text-admin-slate-300">
                        Enable Sale
                      </label>
                      <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                        Activate this sale immediately when created
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="isSaleActive"
                      checked={saleForm.isSaleActive}
                      onChange={(e) => setSaleForm({ ...saleForm, isSaleActive: e.target.checked })}
                      className="sr-only"
                    />
                    <label
                      htmlFor="isSaleActive"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        saleForm.isSaleActive ? 'bg-primary-600' : 'bg-admin-slate-300 dark:bg-admin-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          saleForm.isSaleActive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </label>
                  </div>
                </div>

                </form>
              </div>

              {/* Modal Footer - Fixed */}
              <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-admin-slate-200 dark:border-admin-slate-700 flex-shrink-0 bg-admin-slate-50 dark:bg-admin-slate-800/50">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 sm:flex-none px-6 py-3 text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 bg-white dark:bg-admin-slate-700 border border-admin-slate-300 dark:border-admin-slate-600 hover:bg-admin-slate-50 dark:hover:bg-admin-slate-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="sale-form"
                  className="flex-1 sm:flex-none px-6 py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {editingSale 
                    ? 'Update Sale' 
                    : isBulkMode 
                      ? `Create Sales (${selectedProducts.length})` 
                      : 'Create Sale'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPage;
